import { useState, useRef, useEffect } from 'react';
import {
  FileText,
  CheckCircle2,
  Clock,
  Trash2,
  Pencil,
  Check,
  X,
  GraduationCap,
  Briefcase,
  BookOpen,
  Lock,
  Eye,
} from 'lucide-react';
import { useReports } from '@contexts/ReportsContext';
import ConfirmModal from '@components/common/ConfirmModal';

const PROFILE_ICONS = {
  student: BookOpen,
  fresher: GraduationCap,
  professional: Briefcase,
};

const PROFILE_COLORS = {
  student: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-600',
    badge: 'bg-blue-100 text-blue-700',
    active: 'border-blue-500 bg-blue-50 shadow-blue-100',
  },
  fresher: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    icon: 'text-orange-600',
    badge: 'bg-orange-100 text-orange-700',
    active: 'border-orange-500 bg-orange-50 shadow-orange-100',
  },
  professional: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    icon: 'text-purple-600',
    badge: 'bg-purple-100 text-purple-700',
    active: 'border-purple-500 bg-purple-50 shadow-purple-100',
  },
  default: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    icon: 'text-gray-500',
    badge: 'bg-gray-100 text-gray-600',
    active: 'border-secondary-500 bg-secondary-50 shadow-secondary-100',
  },
};

const formatDate = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const ReportCard = ({ report }) => {
  const { activeReportId, switchReport, renameReport, deleteReport } =
    useReports();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(report.reportName);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const inputRef = useRef(null);

  const isActive = report.id === activeReportId;
  const colors = PROFILE_COLORS[report.profileType] || PROFILE_COLORS.default;
  const ProfileIcon = PROFILE_ICONS[report.profileType] || FileText;
  const isCompleted = report.status === 'completed';

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleRename = async () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== report.reportName) {
      await renameReport(report.id, trimmed);
    } else {
      setEditName(report.reportName);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleRename();
    if (e.key === 'Escape') {
      setEditName(report.reportName);
      setIsEditing(false);
    }
  };

  const progressPct = report.totalSteps > 0
    ? Math.round((report.completedSteps / report.totalSteps) * 100)
    : 0;

  const handleSwitchReport = async () => {
    if (!isEditing && !showDeleteConfirm) {
      await switchReport(report.id);
    }
  };

  return (
    <div
      onClick={handleSwitchReport}
      className={`
        relative group w-full rounded-xl border p-3 sm:p-4 cursor-pointer
        transition-all duration-200
        ${isActive
          ? `${colors.active} shadow-md`
          : !report.isEditable
          ? 'border-red-200 bg-red-50 hover:shadow-sm hover:border-red-300'
          : `${colors.border} bg-white hover:shadow-sm hover:border-gray-300`
        }
      `}
    >
      {/* Active indicator strip */}
      {isActive && (
        <div className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full bg-secondary-500" />
      )}

      <div className="flex items-start gap-2 pl-1">
        {/* Icon */}
        {/* <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${colors.bg}`}>
          <ProfileIcon className={`w-4 h-4 ${colors.icon}`} />
        </div> */}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Inline edit row — shown when editing */}
          {isEditing ? (
            <div
              className="flex items-center gap-1 mb-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                ref={inputRef}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleRename}
                className="flex-1 text-sm text-gray-900 bg-white border border-secondary-400 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-secondary-500 min-w-0"
              />
              <button
                onMouseDown={(e) => { e.preventDefault(); handleRename(); }}
                className="p-1 rounded hover:bg-green-50 text-green-600 flex-shrink-0"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                onMouseDown={(e) => { e.preventDefault(); setEditName(report.reportName); setIsEditing(false); }}
                className="p-1 rounded hover:bg-red-50 text-red-500 flex-shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <>
              {/* Report name - full display */}
              <h3 className="text-sm font-bold text-gray-900 mb-1 line-clamp-2" title={report.reportName}>
                {report.reportName}
              </h3>

              {/* Profile type + date + status badges */}
              <div className="flex items-center gap-1.5 flex-wrap mb-2">
                {report.profileType && (
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${colors.badge}`}>
                    {report.profileType.charAt(0).toUpperCase() + report.profileType.slice(1)}
                  </span>
                )}
                <span className="text-[10px] text-gray-400">
                  {formatDate(report.createdAt)}
                </span>
              </div>

              {/* Status badges row */}
              <div className="flex items-center gap-1.5 flex-wrap mb-2">
                {!report.isEditable && (
                  <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-700 text-[10px] font-semibold">
                    <Eye className="w-3 h-3" />
                    View Only
                  </span>
                )}
                {isCompleted ? (
                  <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-green-100 text-green-700 text-[10px] font-semibold">
                    <CheckCircle2 className="w-3 h-3" />
                    Done
                  </span>
                ) : (
                  <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[10px] font-semibold">
                    <Clock className="w-3 h-3" />
                    In Progress
                  </span>
                )}
              </div>
            </>
          )}

          {/* Progress bar */}
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  isCompleted ? 'bg-green-500' : 'bg-secondary-500'
                }`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span className="text-[10px] text-gray-500 flex-shrink-0">
              {report.completedSteps}/{report.totalSteps}
            </span>
          </div>
        </div>

        {/* Action buttons — visible on hover or when active */}
        {!isEditing && !showDeleteConfirm && report.isEditable && (
          <div
            className={`flex-shrink-0 flex items-center gap-1 transition-opacity ${
              isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
              title="Rename report"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
              title="Delete report"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        
        {/* Lock icon for view-only reports */}
        {!isEditing && !showDeleteConfirm && !report.isEditable && (
          <div className="flex-shrink-0 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Lock className="w-3.5 h-3.5 text-red-600" title="View only - Upgrade plan to edit" />
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
              title="Delete report"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Delete confirmation modal */}
        <ConfirmModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={async () => {
            await deleteReport(report.id);
            setShowDeleteConfirm(false);
          }}
          title="Delete this report?"
          message="This action cannot be undone. The report will be permanently removed."
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
        />
      </div>
    </div>
  );
};

export default ReportCard;
